import { Modal, ModalProps } from '@/modules/common/components/Modal';
import { ApiErrorResponse } from '@/types/api';
import { ITeam, ITeamPayload } from '@/types/team';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  FormControl,
  FormLabel,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useCreateTeam, useTeamMutation } from '../../hooks/useTeams';

const FORM_SCHEMA = yup.object().shape({
  name: yup.string().required('name is required'),
  description: yup.string().optional(),
});

type TeamDialog = Omit<ModalProps, 'children' | 'title' | 'subTitle'> & {
  team?: ITeam;
  onSuccess?: (teamName?: string) => void;
};

export function TeamDialog({ team, onSuccess, ...props }: TeamDialog) {
  const isEditing = !!team;

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ITeamPayload>({
    resolver: yupResolver(FORM_SCHEMA),
    defaultValues: {
      name: team?.name || '',
      description: team?.description || '',
    },
  });

  const { mutateAsync: createTeam, isPending: isCreating } = useCreateTeam();
  const { mutateAsync: updateTeam, isPending: isUpdating } = useTeamMutation();

  const isPending = isCreating || isUpdating;

  const handleSubmitRequest = async (data: ITeamPayload) => {
    try {
      if (isEditing && team) {
        await updateTeam({
          id: team._id,
          updatedData: {
            name: data.name,
            description: data.description,
            learningBudgetLimit: team.learningBudgetLimit || 0,
          },
        });
        onSuccess?.(data.name);
        enqueueSnackbar(`Team ${data.name} updated successfully`, {
          variant: 'success',
        });
      } else {
        await createTeam(data);
        onSuccess?.(data.name);
        enqueueSnackbar(`Team ${data.name} created successfully`, {
          variant: 'success',
        });
      }
    } catch (error) {
      if ((error as ApiErrorResponse).statusCode === 400) {
        return setError('root.serverError', {
          type: 'manual',
          message:
            (error as ApiErrorResponse).message ??
            'An error occurred. Please try again later.',
        });
      }
      enqueueSnackbar('An error occurred. Please try again later.', {
        variant: 'error',
      });
    }
  };

  const handleCancel = () => {
    props.onClose?.({}, 'backdropClick');
  };

  return (
    <Modal
      {...props}
      title={isEditing ? `Edit team` : `Create a new team`}
      subTitle={
        isEditing
          ? "Update the team's information below."
          : 'A team is a department of group, each having its own lists and settings.'
      }
      onClose={(...args) => {
        reset();
        props.onClose?.(...args);
      }}
      classes={{
        paper: 'max-w-xl',
      }}
      actions={
        <>
          <Button
            variant="outlined"
            size="small"
            type="button"
            onClick={handleCancel}
            className="w-32 mt-6"
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            type="submit"
            className="w-32 mt-6"
            form="team-form"
            loading={isSubmitting}
          >
            {isEditing ? 'Update' : 'Confirm'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={handleSubmit(handleSubmitRequest)}
        className="flex flex-col justify-end gap-3"
        id="team-form"
      >
        {errors.root?.serverError?.message && (
          <ul className="text-red-500 list-disc">
            {errors.root.serverError.message?.split(';').map((error, index) => (
              <li key={index}>
                {error}
                <br />
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-col gap-6">
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <FormLabel htmlFor="name">Team Name</FormLabel>
                <TextField
                  {...(field as TextFieldProps)}
                  id="name"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                />
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <FormControl fullWidth>
                <FormLabel htmlFor="description">
                  Description (optional)
                </FormLabel>
                <TextField
                  {...field}
                  rows={3}
                  multiline
                  type="text"
                  id="description"
                  placeholder=""
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              </FormControl>
            )}
          />
        </div>
      </form>
    </Modal>
  );
}
